import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core'
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSelectModule } from '@angular/material/select'
import { ToasterService } from '@core/services/toaster.service'
import Keycloak from 'keycloak-js'
import { RecaptchaFormsModule, RecaptchaLoaderService, RecaptchaModule } from 'ng-recaptcha-2'
import { FeedbackService } from './feedback.service'

export const ISSUE_LEVELS = ['Low', 'Normal', 'High'] as const
export const ISSUE_TYPES = ['Bug', 'Question', 'Enhancement'] as const
@Component({
  selector: 'app-dialog-feedback',
  templateUrl: 'dialog-feedback.component.html',
  styleUrls: ['dialog-feedback.component.scss'],
  providers: [FeedbackService, RecaptchaLoaderService],
  imports: [
    // Forms
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    // Material
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatCheckboxModule,
    // Captcha
    RecaptchaModule,
    RecaptchaFormsModule,
  ],
})
export class DialogFeedbackComponent {
  @ViewChild('file_input') file_input!: ElementRef

  @Input({
    required: true,
  })
  @Output()
  closeForm = new EventEmitter<void>()

  private readonly _dialogRef = inject(MatDialogRef<DialogFeedbackComponent>)
  readonly keycloak = inject(Keycloak)
  private readonly _feedbackService = inject(FeedbackService)
  private readonly _toasterService = inject(ToasterService)

  captcha: string | null = null
  captchaError = false
  formError = false

  feedbackForm = new FormGroup({
    name: new FormControl(),
    email: new FormControl('', [Validators.required, Validators.email]),
    company: new FormControl(),
    type: new FormControl('', [Validators.required]),
    level: new FormControl('', [Validators.required]),
    title: new FormControl('', [Validators.required]),
    description: new FormControl('', [Validators.required]),
    file: new FormControl(),
    isAccepted: new FormControl(false, [Validators.requiredTrue]),
  })

  imageToUpload: File | null = null

  readonly issueLevels = ISSUE_LEVELS
  readonly issueTypes = ISSUE_TYPES

  feedbackLoader = false
  fileName: string = ''

  constructor() {
    if (this.keycloak.authenticated) {
      const email = this.keycloak.tokenParsed?.email
      const firstname = this.keycloak.tokenParsed?.given_name
      const lastname = this.keycloak.tokenParsed?.family_name
      const name = `${firstname} ${lastname}`.trim()

      this.feedbackForm.patchValue({
        email: email,
        name: name,
      })
    }
  }

  onCaptchaResolved(captchaResponse: string) {
    this.captcha = captchaResponse
    this.captchaError = false
  }

  onCaptchaExpired() {
    this.captcha = null
    this.captchaError = true
  }

  getFileOnLoad(event: Event) {
    const input = event.target as HTMLInputElement
    if (input.files && input.files.length > 0) {
      this.imageToUpload = input.files[0]
    }
  }

  async submitFeedbackForm() {
    this.feedbackLoader = true

    if (!this.feedbackForm.valid) {
      this.formError = true
      this.feedbackLoader = false
      return
    }

    this.formError = false

    if (!this.captcha) {
      this.captchaError = true
      this.feedbackLoader = false
      return
    }
    this.captchaError = false

    const fd = new FormData()
    const formValue = this.feedbackForm.value

    if (formValue.file && this.imageToUpload) {
      fd.append('file', this.imageToUpload, this._removeSpecialCharacters(this.imageToUpload.name))
    }
    fd.append('name', formValue.name)
    fd.append('email', formValue.email ?? '')
    fd.append('company', formValue.company)
    fd.append('feedback_type', formValue.type ?? '')
    fd.append('feedback_priority', formValue.level ?? '')
    fd.append('title', formValue.title ?? '')
    fd.append('description', formValue.description ?? '')
    fd.append('captcha', this.captcha)
    const response = await this._feedbackService.sendFeedback(fd)
    this.feedbackLoader = false

    if (response === null) return

    this._toasterService.openSnackBar(response.message)

    this._dialogRef.close(false)
  }

  private _removeSpecialCharacters(str: string) {
    return str.replace(/[^a-zA-Z0-9\s._-]/g, '')
  }
}
